import clsx from 'clsx';
import React, { forwardRef } from 'react';

import { ConditionalWrapper } from '../ConditionalWrapper';
import { Icon } from '../Icon';
import { VisuallyHidden } from '../VisuallyHidden';

import type { ForwardedRef } from 'react';
import type { IconProps } from '../Icon/Icon.types';
import type {
  ButtonProps,
  DefaultButtonProps,
  LinkButtonProps,
} from './Button.types';

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      buttonLabel,
      buttonSize = 'Default',
      buttonType = 'Primary',
      children,
      classNames,
      disabled = false,
      hideText,
      href,
      icon,
      iconLeft,
      iconRight,
      inverted,
      loading = false,
      testId = `button-${buttonType}`,
      ...restProps
    },
    ref
  ) => {
    const buttonClassNames = clsx(
      'button',
      {
        button_loading: loading,
        button_primary: buttonType === 'Primary',
        button_primary_medium:
          buttonType === 'Primary' && buttonSize === 'Medium',
        button_primary_large:
          buttonType === 'Primary' && buttonSize === 'Large',
        'button_primary--invert': buttonType === 'Primary' && inverted,
        button_secondary: buttonType === 'Secondary',
        'button_secondary--medium':
          buttonType === 'Secondary' && buttonSize === 'Medium',
        'button_secondary--large':
          buttonType === 'Secondary' && buttonSize === 'Large',
        button_specialized: buttonType === 'Specialized',
        'button-outline': buttonType === 'Outline',
        'button-outline_medium':
          buttonType === 'Outline' && buttonSize === 'Medium',
        'button-outline_large':
          buttonType === 'Outline' && buttonSize === 'Large',
        'button-outline_invert': buttonType === 'Outline' && inverted,
        'button-outline_dark': buttonType === 'Dark Outline',
        'button-outline_dark_medium':
          buttonType === 'Dark Outline' && buttonSize === 'Medium',
        'button-outline_dark_large':
          buttonType === 'Dark Outline' && buttonSize === 'Large',
        button_dark: buttonType === 'Dark',
        button_dark_medium: buttonType === 'Dark' && buttonSize === 'Medium',
        button_dark_large: buttonType === 'Dark' && buttonSize === 'Large',
      },
      classNames
    );
    const renderIcon = (icon: IconProps): JSX.Element => (
      <Icon
        {...icon}
        className={clsx(icon.className, {
          'button_icon-left': iconLeft,
          'button_icon-right': iconRight,
          button_specialized__icon: buttonType === 'Specialized',
        })}
      />
    );

    const content = (
      <>
        {!!iconLeft && renderIcon(iconLeft)}
        {!!icon && renderIcon(icon)}
        {children ?? (!hideText && buttonLabel)}
        {!!iconRight && renderIcon(iconRight)}
      </>
    );

    return (
      <ConditionalWrapper
        condition={
          buttonType === 'Secondary' && Boolean(iconLeft || iconRight || icon)
        }
        wrapper={(child: React.ReactNode) => (
          <div className='button-wrapper'>{child}</div>
        )}
      >
        {href ? (
          <a
            href={href}
            className={buttonClassNames}
            ref={ref as ForwardedRef<HTMLAnchorElement>}
            data-testid={testId}
            {...(restProps as LinkButtonProps)}
          >
            {loading ? (
              <VisuallyHidden>loading</VisuallyHidden>
            ) : (
              hideText &&
              buttonLabel && <VisuallyHidden>{buttonLabel}</VisuallyHidden>
            )}
            {content}
          </a>
        ) : (
          // eslint-disable-next-line react/button-has-type
          <button
            disabled={disabled || loading}
            className={buttonClassNames}
            ref={ref as ForwardedRef<HTMLButtonElement>}
            data-testid={testId}
            {...(restProps as DefaultButtonProps)}
          >
            {loading ? (
              <VisuallyHidden>loading</VisuallyHidden>
            ) : (
              hideText &&
              buttonLabel && <VisuallyHidden>{buttonLabel}</VisuallyHidden>
            )}
            {content}
          </button>
        )}
      </ConditionalWrapper>
    );
  }
);
